import React from "react";
import { Box, Typography } from "@mui/material";
import LineupSlot from "../fanPage/LineupSlot";
import { colors } from "../../theme";
import {
  FIELD_ASPECT,
  FIELD_SURFACE,
  NFL_FIELD_LABELS,
  NFL_LINE_OF_SCRIMMAGE_Y,
  getSportFieldPositions,
  groupSlotsForCompact,
  slotPointSx,
} from "../../utils/sportField";

const svgSx = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
};

const NbaMarkings = () => (
  <Box component="svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden sx={svgSx}>
    <rect x="38" y="8" width="24" height="30" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.16)" strokeWidth="0.45" />
    <circle cx="50" cy="38" r="8" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="0.4" />
    <circle cx="50" cy="10" r="1.6" fill="rgba(255,255,255,0.28)" />
    <path
      d="M 18,10 Q 18,58 50,62 Q 82,58 82,10"
      fill="none"
      stroke="rgba(255,255,255,0.14)"
      strokeWidth="0.45"
    />
    <line x1="8" y1="8" x2="92" y2="8" stroke="rgba(255,255,255,0.18)" strokeWidth="0.5" />
    <line x1="8" y1="86" x2="92" y2="86" stroke="rgba(255,255,255,0.16)" strokeWidth="0.45" />
    <path
      d="M 42,86 A 8,8 0 0 1 58,86"
      fill="none"
      stroke="rgba(255,255,255,0.12)"
      strokeWidth="0.4"
    />
  </Box>
);

const sidelineLabelSx = (point) => ({
  position: "absolute",
  left: `${point.x}%`,
  top: `${point.y}%`,
  transform: "translate(-50%, -50%) rotate(-90deg)",
  color: "rgba(255,255,255,0.28)",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.16em",
  pointerEvents: "none",
  userSelect: "none",
});

const NflMarkings = () => (
  <Box aria-hidden sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
    <Box
      sx={{
        position: "absolute",
        left: "8%",
        right: "8%",
        top: `${NFL_LINE_OF_SCRIMMAGE_Y}%`,
        height: 2,
        bgcolor: "rgba(255,255,255,0.28)",
        transform: "translateY(-50%)",
      }}
    />
    <Box sx={sidelineLabelSx(NFL_FIELD_LABELS.defense)}>DEFENSE</Box>
    <Box sx={sidelineLabelSx(NFL_FIELD_LABELS.offense)}>OFFENSE</Box>
  </Box>
);

const CompactSlots = ({ sport, slots, players, editable, onSelect, onClear }) => (
  <Box>
    {groupSlotsForCompact(sport, slots).map((group) => (
      <Box key={group.id} sx={{ mb: group.label ? 2 : 0 }}>
        {group.label && (
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.12em" }}>
            {group.label}
          </Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 2,
            py: 1,
          }}
        >
          {group.slots.map((slot) => (
            <Box key={slot.id} sx={{ display: "flex", justifyContent: "center" }}>
              <LineupSlot
                slot={slot}
                player={players?.[slot.id]}
                editable={editable}
                size="battery"
                sport={sport}
                onSelect={onSelect}
                onClear={onClear}
              />
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

const SportFormationField = ({
  sport,
  slots = [],
  players,
  editable,
  compact = false,
  onSelect,
  onClear,
}) => {
  const positions = getSportFieldPositions(sport);

  if (!positions || compact) {
    return (
      <CompactSlots
        sport={sport}
        slots={slots}
        players={players}
        editable={editable}
        onSelect={onSelect}
        onClear={onClear}
      />
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        bgcolor: FIELD_SURFACE[sport] || FIELD_SURFACE.nba,
        px: { md: 1.5 },
        py: 2,
      }}
    >
      <Box sx={{ px: { md: 1 }, py: { md: 2.5 } }}>
        <Box
          sx={{
            position: "relative",
            aspectRatio: FIELD_ASPECT[sport] || "3 / 4",
            width: "100%",
            maxWidth: sport === "nfl" ? 640 : 520,
            mx: "auto",
            overflow: "visible",
          }}
        >
          {sport === "nba" ? <NbaMarkings /> : <NflMarkings />}
          {slots.map((slot) => (
            <Box key={slot.id} sx={slotPointSx(positions[slot.id])}>
              <LineupSlot
                slot={slot}
                player={players?.[slot.id]}
                editable={editable}
                size={sport === "nfl" ? "mini" : "field"}
                pinToHeadshot
                sport={sport}
                onSelect={onSelect}
                onClear={onClear}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SportFormationField;
